<template>
    <router-link class="cursor-pointer group block" :to="link">
        <div v-if="postLayout === 'big_image'" class="post p-4 rounded-md relative overflow-hidden">
            <div
                :style="`background-image: url(${postImage})`"
                class="absolute top-0 left-0 w-full h-full group-hover:scale-110 transition-all ease-in-out duration-500 bg-cover bg-no-repeat bg-center"
            ></div>
            <div class="overlay-bottom-dark absolute top-0 bottom-0 left-0 right-0 w-full h-full z-[5]" />

            <div class="absolute top-0 left-0 w-full h-full p-4 flex items-end text-white z-[10] group-hover:backdrop-blur-sm group-hover:bg-blue-dark/30">
                <div>
                    <small class="block">
                        {{ formatDate(contentstream.published_at) }}
                    </small>
                    <h3 class="base_bold mt-2 block">
                        {{ $translate(contentstream.title) }}
                    </h3>
                    <div
                        v-html="$translate(contentstream.teaser)"
                        class="font-thin mt-2 max-h-0 opacity-0 group-hover:opacity-100 group-hover:max-h-[400px] transition-all ease-in-out duration-500 line-clamp-4"
                    ></div>
                    <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                        <span>
                            {{ $t('read_more') }}
                        </span>
                        <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="postLayout === 'small_image'" class="post group p-4 rounded-md relative overflow-hidden">
            <div class="flex flex-row gap-4 h-full">
                <div class="w-1/4 flex items-center justify-start">
                    <img :alt="$translate(contentstream.title)" :src="postImage" ref="image" class="h-full w-full rounded-md object-cover" />
                </div>
                <div class="w-3/4 flex flex-col justify-between">
                    <small class="block">
                        {{ formatDate(contentstream.published_at) }}
                    </small>
                    <h3 class="base_bold mt-2 line-clamp-1">
                        {{ $translate(contentstream.title) }}
                    </h3>
                    <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                        <span>
                            {{ $t('read_more') }}
                        </span>
                        <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="postLayout === 'tiny_image'" class="post group p-4 rounded-md relative overflow-hidden">
            <div class="flex flex-col justify-between h-full">
                <div>
                    <small class="block">{{ formatDateByRegion($moment(contentstream.published_at)) }}</small>
                    <h3 class="base_bold mt-2 block">{{ $translate(contentstream.title) }}</h3>
                    <div class="mt-2">
                        <img :alt="$translate(contentstream.title)" :src="postImage" ref="image" class="w-36 h-24 float-left mr-3 rounded-lg object-cover" />
                        <!-- <img class="w-36 h-24 float-left mr-3 rounded-lg object-cover" :src="$asset('/hkcm/images/banner/blackfriday.png', 'public')" alt="HKCM News" title="HKCM News"/> -->
                        <p v-html="$translate(contentstream.teaser).slice(0, 370) + '...'" class="font-thin"></p>
                    </div>
                </div>
                <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                    <span>{{ $t('read_more') }}</span>
                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                </div>
                <div class="flex flex-row border-t border-gray-300 my-4 pt-4 gap-4" v-if="contentstream.postCategories.data.length">
                    <div v-for="category in contentstream.postCategories.data.slice(0, 5)" :key="category" class="rounded-full bg-blue-light p-2 flex flex-row gap-2 items-center">
                        <div class="w-4 h-4 rounded-full bg-blue"></div>
                        <span class="text-xs text-blue-dark">{{ $translate(category.name) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="postLayout === 'no_image'" class="post group p-4 rounded-md relative overflow-hidden">
            <div class="flex flex-col justify-between h-full">
                <div>
                    <small class="block">
                        {{ formatDate(contentstream.published_at) }}
                    </small>
                    <h3 class="base_bold mt-2 block">
                        {{ $translate(contentstream.title) }}
                    </h3>
                    <div v-html="$translate(contentstream.teaser)" class="font-thin mt-2 line-clamp-11 self-start"></div>
                </div>
                <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                    <span>
                        {{ $t('read_more') }}
                    </span>
                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                </div>
                <div class="flex flex-row border-t border-gray-300 my-4 pt-4 gap-4" v-if="contentstream.postCategories.data.length">
                    <div v-for="category in contentstream.postCategories.data.slice(0, 5)" :key="category" class="rounded-full bg-blue-light p-2 flex flex-row gap-2 items-center">
                        <div class="w-4 h-4 rounded-full bg-blue" />
                        <span class="text-xs text-blue-dark">
                            {{ $translate(category.name) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="postLayout === 'big_image_tags'" class="post p-4 rounded-md relative overflow-hidden">
            <div
                :style="`background-image: url(${postImage})`"
                class="absolute top-0 left-0 w-full h-full group-hover:scale-110 transition-all ease-in-out duration-500 bg-cover bg-no-repeat bg-center"
            ></div>
            <div class="overlay-bottom-dark absolute top-0 bottom-0 left-0 right-0 w-full h-full z-[5]" />

            <div class="absolute top-0 left-0 w-full h-full p-4 flex items-end text-white z-[10] group-hover:backdrop-blur-sm group-hover:bg-blue-dark/30">
                <div class="w-full">
                    <small class="block">
                        {{ formatDate(contentstream.published_at) }}
                    </small>
                    <h3 class="base_bold mt-2 block line-clamp-3 min-h-[72px] group-hover:min-h-[0px] duration-500 transition-all ease-in-out">
                        {{ $translate(contentstream.title) }}
                    </h3>

                    <div
                        v-html="$translate(contentstream.teaser)"
                        class="max-h-0 group-hover:max-h-[400px] line-clamp-4 transition-all ease-in-out duration-500 overflow-hidden delay-500"
                    ></div>

                    <div class="max-h-0 group-hover:max-h-[400px] transition-all ease-in-out duration-500 overflow-hidden delay-500">
                        <div class="flex flex-row justify-between border-t border-gray-300 my-4 pt-4 gap-4">
                            <div class="flex flex-row" v-if="contentstream.postCategories.data.length">
                                <div
                                    v-for="category in contentstream.postCategories.data.slice(0, 5)"
                                    :key="category"
                                    class="rounded-full bg-blue-light p-2 flex flex-row gap-2 items-center"
                                >
                                    <div class="w-4 h-4 rounded-full bg-blue" />
                                    <span class="text-xs text-blue-dark">
                                        {{ $translate(category.name) }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                                <span>
                                    {{ $t('read_more') }}
                                </span>
                                <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </router-link>
</template>

<script>
import { mapGetters } from 'vuex';
import i18next from 'i18next';
import { formatDateByRegion } from '../../helpers.js';

export default {
    props: ['contentstream', 'layout', 'height', 'image'],

    data: () => ({
        postLayout: null,
        postHeight: null,
        postImage: null,
        postLayoutHeight: {
            big_image: '450px',
            small_image: '130px',
            no_image: '100%',
            big_image_tags: '600px'
        }
    }),

    methods: {
        formatDateByRegion,
        formatDate(date) {
            return formatDateByRegion(this.$moment(date));
        }
    },

    mounted() {
        if (!this.contentstream.image) {
            this.postLayout = 'no_image';
        } else {
            this.layout ? (this.postLayout = this.layout) : (this.postLayout = this.contentstream.layout);
        }

        this.height ? (this.postHeight = this.height) : (this.postHeight = this.postLayoutHeight[this.postLayout]);

        // NOTE: Set image, use compressed (lg) image if available
        this.contentstream.image_lg ? (this.postImage = this.contentstream.image_lg) : this.image ? (this.postImage = this.image) : (this.postImage = this.contentstream.image);

        // set image height to width
        this.$nextTick(() => {
            if (this.$refs.image) {
                this.$refs.image.clientHeight > this.$refs.image.clientWidth
                    ? (this.$refs.image.style.height = this.$refs.image.clientWidth + 'px')
                    : (this.$refs.image.style.width = this.$refs.image.clientHeight + 'px');
            }
        });
    },

    computed: {
        link() {
            return this.getApplicationType() === 'backend' ? '/dashboard/hkcmnews/' + this.contentstream.slug.name : '/hkcmnews/' + this.contentstream.slug.name;
        },
        ...mapGetters('settings', {
            getApplicationType: 'getApplicationType'
        })
    }
};
</script>

<style scoped>
.post {
    height: v-bind(postHeight);
}
</style>
